import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { capitalised, type BlockNoun } from "../lib/blockNoun";
import {
  blockNounForVersion,
  eligibleEvaluation,
  getRun,
  getRunBaseline,
  listActions,
  listFindings,
  listOrganisations,
  runInput,
} from "../lib/api";
import { inputFingerprint } from "../lib/fingerprint";
import {
  buildComparisonModel,
  type ComparisonModel,
  type ComparisonSideInput,
  type FindingComparison,
  type SideCurrency,
  type SideIdentityPanel,
} from "../lib/comparison";
import type { ActionRow, FindingRow, Organisation, Run } from "../lib/types";
import { useBreadcrumb } from "../shell/ContextHeader";
import { StateBadge } from "./RunOverview";

// The reassessment comparison (F4.6) — a DERIVED view over two recorded
// sides, computed at render and stored nowhere.
//
// What this screen must get right (design §§4-5, revision 2):
//   - the identity panels render in EVERY outcome — refusals stay
//     explainable and auditable (Amendment 3);
//   - movement renders only under `identical_definition`; any mismatch or
//     unreadable fact is `not_comparable`, never a partial view;
//   - lifecycle refusals (withdrawn/superseded/ineligible) fail closed at
//     render, and a superseded baseline names its successor (OQ1);
//   - the comparison is visibly PROVISIONAL while the current run is not
//     finalized/published (OQ2);
//   - currency is computed per side with the standing fingerprint rules,
//     and unknown never presents as current;
//   - the register context is CURRENT operational context, not frozen
//     comparison history (ruling §6).

const num = (n: number | null): string =>
  n === null ? "—" : Number.isInteger(n) ? String(n) : n.toFixed(2);

const delta = (n: number | null): string =>
  n === null ? "—" : n > 0 ? `+${n.toFixed(2)}` : n.toFixed(2);

type State =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "no-baseline"; org: Organisation; run: Run }
  | {
      kind: "ready";
      org: Organisation;
      run: Run;
      model: ComparisonModel;
      openFindings: FindingRow[];
      openActions: ActionRow[];
      noun: BlockNoun;
    };

async function currencyOf(run: Run, inputHash: string | null): Promise<SideCurrency | null> {
  if (inputHash === null) return null;
  try {
    const fp = await inputFingerprint(await runInput(run.id));
    return fp === inputHash ? { kind: "current" } : { kind: "stale" };
  } catch (e) {
    return { kind: "unknown", reason: (e as Error).message };
  }
}

export function RunComparison() {
  const { orgId, runId } = useParams<{ orgId: string; runId: string }>();
  const { setTrail } = useBreadcrumb();
  const [state, setState] = useState<State>({ kind: "loading" });

  const load = useCallback(async () => {
    if (!orgId || !runId) return;
    try {
      const [orgs, run, binding] = await Promise.all([
        listOrganisations(),
        getRun(runId),
        getRunBaseline(runId),
      ]);
      const org = orgs.find((o) => o.id === orgId);
      if (!org || !run || run.organisation_id !== orgId) {
        setState({ kind: "error", message: "run not found, or you are not a member" });
        return;
      }
      const trail = [
        { label: "Clients & engagements", to: "/clients" },
        { label: org.name, to: `/clients/${org.id}` },
        { label: run.title ?? "Untitled run", to: `/clients/${org.id}/runs/${run.id}` },
        { label: "Comparison" },
      ];
      if (!binding) {
        setState({ kind: "no-baseline", org, run });
        setTrail(trail);
        return;
      }
      const baselineRun = await getRun(binding.baseline_run_id);
      if (!baselineRun) {
        setState({ kind: "error", message: "the bound baseline run could not be read" });
        return;
      }
      // Movement renders only for an identical definition, so the current
      // run's pinned version names the blocks for both sides.
      const [baseEval, currEval, openFindings, actions, noun] = await Promise.all([
        eligibleEvaluation(baselineRun.id),
        eligibleEvaluation(run.id),
        listFindings(runId),
        listActions(orgId),
        blockNounForVersion(run.instrument_version_id),
      ]);
      const [baseCurrency, currCurrency] = await Promise.all([
        currencyOf(baselineRun, baseEval?.input_hash ?? null),
        currencyOf(run, currEval?.input_hash ?? null),
      ]);
      const baseline: ComparisonSideInput = {
        run: baselineRun,
        evaluation: baseEval,
        currency: baseCurrency,
      };
      const current: ComparisonSideInput = {
        run,
        evaluation: currEval,
        currency: currCurrency,
      };
      setState({
        kind: "ready",
        org,
        run,
        model: buildComparisonModel(baseline, current),
        openFindings: openFindings.filter((f) => f.status === "open"),
        openActions: actions.filter((a) => a.status === "open"),
        noun,
      });
      setTrail(trail);
    } catch (e) {
      setState({ kind: "error", message: (e as Error).message });
    }
  }, [orgId, runId, setTrail]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.kind === "loading") return <p>Loading…</p>;
  if (state.kind === "error")
    return <div className="error-banner">Could not load: {state.message}</div>;
  if (state.kind === "no-baseline")
    return (
      <div className="card empty-state">
        <h3>No baseline selected</h3>
        <p>
          This run has no reassessment baseline. Bind one on the{" "}
          <Link to={`/clients/${state.org.id}/runs/${state.run.id}`}>run overview</Link>.
        </p>
      </div>
    );

  const { org, model, openFindings, openActions, noun } = state;
  const { outcome } = model;

  return (
    <>
      <div style={{ display: "flex", alignItems: "baseline", gap: "0.75rem", flexWrap: "wrap" }}>
        <h1 style={{ marginBottom: 0 }}>Reassessment comparison</h1>
        {model.provisional && (
          <span
            className="badge"
            data-testid="provisional-badge"
            style={{ background: "var(--lf-amber-20)", color: "var(--lf-amber-deep)" }}
          >
            provisional — the current run is not finalized
          </span>
        )}
      </div>

      <CurrencyBanner label="Baseline" currency={model.baseline.currency} />
      <CurrencyBanner label="Current run" currency={model.current.currency} />

      <OutcomeBanner model={model} orgId={org.id} />

      {/* The identity block folded to one line (UI review slice 4). Both
          panels stay rendered in every outcome, inside the details. */}
      <section className="card" style={{ marginTop: "1rem" }}>
        <p style={{ margin: 0 }}>
          <strong>{model.current.panel.runTitle ?? "Untitled run"}</strong> against
          the baseline <strong>{model.baseline.panel.runTitle ?? "Untitled run"}</strong>
          {" · "}
          <Link to={`/clients/${org.id}/runs/${state.run.id}`}>change…</Link>
        </p>
        <details style={{ marginTop: "0.5rem" }}>
          <summary style={{ cursor: "pointer", color: "var(--text-muted)" }}>
            Technical details
          </summary>
          <p style={{ color: "var(--text-muted)" }}>
            Both sides' complete recorded identities (readiness C5). The
            comparison derives from these facts and stores nothing.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
            <IdentityPanel title="Baseline" panel={model.baseline.panel} orgId={org.id} />
            <IdentityPanel title="Current run" panel={model.current.panel} orgId={org.id} />
          </div>
        </details>
        {outcome.kind === "not_comparable" && (
          <div style={{ marginTop: "0.75rem" }} data-testid="mismatch-list">
            <strong>Differing identity facts:</strong>
            <ul style={{ margin: "0.35rem 0 0" }}>
              {outcome.mismatches.map((f) => (
                <li key={f.label}>
                  {f.label}: <code>{f.baseline ?? "(unreadable)"}</code> vs{" "}
                  <code>{f.current ?? "(unreadable)"}</code>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {outcome.kind === "movement" && <Movement outcome={outcome} noun={noun} />}

      <section className="card" style={{ marginTop: "1rem" }}>
        <h2 style={{ marginTop: 0 }}>Register context</h2>
        <p style={{ marginTop: 0, color: "var(--text-muted)", fontSize: "var(--lf-body-size)" }}>
          Current operational context — the live register, not frozen
          comparison history.
        </p>
        <p style={{ margin: 0 }}>
          <strong>{openFindings.length}</strong> open managed finding(s) on the
          current run; <strong>{openActions.length}</strong> open action(s) in
          this tenant.{" "}
          <Link to={`/clients/${org.id}/runs/${state.run.id}/register`}>
            Open the findings &amp; actions register
          </Link>
        </p>
      </section>
    </>
  );
}

function CurrencyBanner({
  label,
  currency,
}: {
  label: string;
  currency: SideCurrency | null;
}) {
  if (currency === null || currency.kind === "current") return null;
  return (
    <div className="error-banner" style={{ marginTop: "0.75rem" }} data-testid="currency-banner">
      {currency.kind === "stale" ? (
        <>
          <strong>{label} is stale.</strong> Responses have changed since its
          evaluation was recorded; re-evaluate before relying on this
          comparison.
        </>
      ) : (
        <>
          <strong>{label} currency unknown.</strong> It could not be
          determined whether the responses still match the evaluation
          ({currency.reason}); this comparison must not be read as current.
        </>
      )}
    </div>
  );
}

function OutcomeBanner({ model, orgId }: { model: ComparisonModel; orgId: string }) {
  const o = model.outcome;
  if (o.kind === "movement")
    return (
      <div
        className="card"
        style={{ marginTop: "1rem", borderLeft: "4px solid var(--state-ok)" }}
        data-testid="outcome-banner"
      >
        <strong>Identical definition.</strong> Both sides were evaluated under
        the same instrument version, definition, policy and engine — movement
        is shown below.
      </div>
    );
  if (o.kind === "not_comparable")
    return (
      <div
        className="card"
        style={{ marginTop: "1rem", borderLeft: "4px solid var(--state-danger)" }}
        data-testid="outcome-banner"
      >
        <strong>Not comparable.</strong> The two runs' recorded identities
        differ, and no reviewed mapping between definitions exists. The
        identities are shown below; no score, finding or criterion movement is
        rendered.
      </div>
    );
  const copy: Record<string, ReactNode> = {
    current_withdrawn: (
      <>
        <strong>No movement.</strong> The current run has been withdrawn — an
        invalidated result cannot anchor a comparison.
      </>
    ),
    baseline_withdrawn: (
      <>
        <strong>No movement.</strong> The baseline has been withdrawn since it
        was selected — an invalidated result cannot anchor a comparison. The
        binding is preserved as a recorded fact.
      </>
    ),
    baseline_superseded: (
      <>
        <strong>No movement.</strong> The baseline has been superseded since it
        was selected
        {"successorRunId" in o && o.successorRunId ? (
          <>
            {" — its successor is "}
            <Link to={`/clients/${orgId}/runs/${o.successorRunId}`}>the superseding run</Link>
          </>
        ) : (
          " and its successor is not recorded"
        )}
        . Select the successor as the baseline to compare. The binding is
        preserved; nothing rebinds automatically.
      </>
    ),
    baseline_missing_evaluation: (
      <>
        <strong>Invariant failure.</strong> The baseline is finalized yet
        carries no comparison-eligible evaluation — its finalization gate
        required one, so this is a data integrity defect to investigate, not
        an empty state.
      </>
    ),
    nothing_to_compare_yet: (
      <>
        <strong>Nothing to compare yet.</strong> The current run has no
        eligible evaluation. Evaluate it to see movement against the baseline.
      </>
    ),
  };
  return (
    <div
      className="card"
      style={{ marginTop: "1rem", borderLeft: "4px solid var(--lf-amber-deep)" }}
      data-testid="outcome-banner"
    >
      {copy[o.reason]}
    </div>
  );
}

function IdentityPanel({
  title,
  panel,
  orgId,
}: {
  title: string;
  panel: SideIdentityPanel;
  orgId: string;
}) {
  const mono = { margin: 0, fontFamily: "var(--lf-font-mono)", overflowWrap: "anywhere" as const, fontSize: "var(--lf-meta-size)" };
  const dt = { color: "var(--text-muted)" };
  return (
    <div data-testid={`identity-${title.toLowerCase().replace(/\s.*$/, "")}`}>
      <h3 style={{ marginTop: 0, marginBottom: "0.35rem" }}>
        <Link to={`/clients/${orgId}/runs/${panel.runId}`}>{panel.runTitle ?? "Untitled run"}</Link>{" "}
        <StateBadge state={panel.runState} />
      </h3>
      <dl style={{ display: "grid", gridTemplateColumns: "max-content 1fr", gap: "0.25rem 0.9rem", margin: 0, fontSize: "var(--lf-meta-size)" }}>
        <dt style={dt}>Run id</dt>
        <dd style={mono}>{panel.runId}</dd>
        <dt style={dt}>Created</dt>
        <dd style={{ margin: 0 }}>{new Date(panel.runCreatedAt).toLocaleString()}</dd>
        <dt style={dt}>Instrument version</dt>
        <dd style={mono}>{panel.instrumentVersionId}</dd>
        <dt style={dt}>Definition hash</dt>
        <dd style={mono}>{panel.definitionHash}</dd>
        {panel.evaluation ? (
          <>
            <dt style={dt}>Evaluation</dt>
            <dd style={mono}>{panel.evaluation.id} (seq {panel.evaluation.seq})</dd>
            <dt style={dt}>Evaluated</dt>
            <dd style={{ margin: 0 }}>{new Date(panel.evaluation.evaluatedAt).toLocaleString()}</dd>
            <dt style={dt}>Qualification</dt>
            <dd style={{ margin: 0 }}>{panel.evaluation.qualificationStatus}</dd>
            <dt style={dt}>Policy</dt>
            <dd style={mono}>{panel.evaluation.policyId}</dd>
            <dt style={dt}>Policy hash</dt>
            <dd style={mono}>{panel.evaluation.policyHash}</dd>
            <dt style={dt}>Engine contract</dt>
            <dd style={mono}>{panel.evaluation.engineContractVersion}</dd>
            <dt style={dt}>Canonicalization</dt>
            <dd style={mono}>{panel.evaluation.canonicalization}</dd>
            <dt style={dt}>Hash algorithm</dt>
            <dd style={mono}>{panel.evaluation.hashAlgorithm}</dd>
            <dt style={dt}>Input hash</dt>
            <dd style={mono}>{panel.evaluation.inputHash}</dd>
            <dt style={dt}>Result hash</dt>
            <dd style={mono}>{panel.evaluation.resultHash}</dd>
          </>
        ) : (
          <>
            <dt style={dt}>Evaluation</dt>
            <dd style={{ margin: 0, color: "var(--text-muted)" }}>no eligible evaluation</dd>
          </>
        )}
      </dl>
    </div>
  );
}

function Movement({
  outcome,
  noun,
}: {
  outcome: Extract<ComparisonModel["outcome"], { kind: "movement" }>;
  noun: BlockNoun;
}) {
  const m = outcome.movement;
  const th = { textAlign: "left" as const, color: "var(--text-muted)", fontWeight: 600, fontSize: "var(--lf-meta-size)" };
  return (
    <>
      <section className="card" style={{ marginTop: "1rem" }} data-testid="overall-movement">
        <h2 style={{ marginTop: 0 }}>Overall movement</h2>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr>
              <th style={th}></th>
              <th style={th}>Baseline</th>
              <th style={th}>Current</th>
              <th style={th}>Δ (decimal)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Mean maturity level (of {m.overall.baselineBlockCount} / {m.overall.currentBlockCount} {noun.many})</td>
              <td>{num(m.overall.baselineMean)}</td>
              <td>{num(m.overall.currentMean)}</td>
              <td>—</td>
            </tr>
            <tr>
              <td>Mean raw score (the decimal — movement inside a level is real movement)</td>
              <td>{num(m.overall.baselineRaw)}</td>
              <td>{num(m.overall.currentRaw)}</td>
              <td data-testid="overall-raw-delta">{delta(m.overall.rawDelta)}</td>
            </tr>
          </tbody>
        </table>

        <h3 style={{ marginBottom: "0.35rem" }}>Verdict</h3>
        <p style={{ margin: 0 }} data-testid="verdict-pair">
          Baseline:{" "}
          {m.verdicts.baseline.verdict ? (
            <strong>level {m.verdicts.baseline.verdict.mean_maturity_level}
              {m.verdicts.baseline.verdict.capped ? " (capped)" : ""}</strong>
          ) : (
            <em>withheld — {m.verdicts.baseline.withheld ?? "no formal verdict"}</em>
          )}
          {" · "}Current:{" "}
          {m.verdicts.current.verdict ? (
            <strong>level {m.verdicts.current.verdict.mean_maturity_level}
              {m.verdicts.current.verdict.capped ? " (capped)" : ""}</strong>
          ) : (
            <em>withheld — {m.verdicts.current.withheld ?? "no formal verdict"}</em>
          )}
        </p>
        {m.verdicts.movement ? (
          <p style={{ margin: "0.35rem 0 0" }} data-testid="verdict-movement">
            Formal verdict movement: level {m.verdicts.movement.from} →{" "}
            level {m.verdicts.movement.to}.
          </p>
        ) : (
          <p style={{ margin: "0.35rem 0 0", color: "var(--text-muted)" }} data-testid="verdict-movement-suppressed">
            No verdict movement is stated: it requires a formal verdict on both
            sides.
          </p>
        )}

        <h3 style={{ marginBottom: "0.35rem" }}>Coverage and evidence (separate claims)</h3>
        <p style={{ margin: 0 }}>
          Coverage: {m.coverage.baseline.criteria_covered}/{m.coverage.baseline.criteria_applicable} →{" "}
          {m.coverage.current.criteria_covered}/{m.coverage.current.criteria_applicable} criteria.
          {" · "}
          Evidence: {m.evidence.baseline.linked}/{m.evidence.baseline.required} →{" "}
          {m.evidence.current.linked}/{m.evidence.current.required} required links.
        </p>
      </section>

      <section className="card" style={{ marginTop: "1rem" }} data-testid="dimension-movement">
        <h2 style={{ marginTop: 0 }}>Dimension movement</h2>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr>
              <th style={th}>Dimension</th>
              <th style={th}>Baseline (n)</th>
              <th style={th}>Current (n)</th>
              <th style={th}>Δ</th>
            </tr>
          </thead>
          <tbody>
            {m.dimensions.map((d) => (
              <tr key={d.dimension}>
                <td>{d.dimension} — {d.title}</td>
                <td>{num(d.baselineMean)} (of {d.baselineCount})</td>
                <td>{num(d.currentMean)} (of {d.currentCount})</td>
                <td>
                  {delta(d.delta)}
                  {d.denominatorChanged && (
                    <span style={{ color: "var(--lf-amber-deep)" }}> — denominators differ</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="card" style={{ marginTop: "1rem" }} data-testid="block-movement">
        <h2 style={{ marginTop: 0 }}>{capitalised(noun.one)} movement</h2>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr>
              <th style={th}>{capitalised(noun.one)}</th>
              <th style={th}>Baseline</th>
              <th style={th}>Current</th>
              <th style={th}>Δ raw</th>
              <th style={th}>Caps</th>
            </tr>
          </thead>
          <tbody>
            {m.blocks.map((b) => (
              <tr key={b.blockId}>
                <td>{b.title}</td>
                <td>{num(b.baselineLevel)} ({num(b.baselineRaw)})</td>
                <td>{num(b.currentLevel)} ({num(b.currentRaw)})</td>
                <td>{delta(b.rawDelta)}</td>
                <td>
                  {b.capReleased && (
                    <span style={{ color: "var(--state-ok)" }} data-testid={`cap-released-${b.blockId}`}>
                      cap released ({b.baselineCaps.map((c) => c.id).join(", ")})
                    </span>
                  )}
                  {b.capIntroduced && (
                    <span style={{ color: "var(--state-danger)" }}>
                      cap introduced ({b.currentCaps.map((c) => c.id).join(", ")})
                    </span>
                  )}
                  {!b.capReleased && !b.capIntroduced &&
                    (b.currentCaps.length > 0
                      ? b.currentCaps.map((c) => c.id).join(", ")
                      : "—")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <details style={{ marginTop: "0.5rem" }}>
          <summary style={{ cursor: "pointer", color: "var(--text-muted)", fontSize: "var(--lf-body-size)" }}>
            Criterion changes (applicability transitions are state changes, not score deltas)
          </summary>
          {m.blocks.filter((b) => b.criterionChanges.length > 0).map((b) => (
            <div key={b.blockId} style={{ marginTop: "0.5rem" }}>
              <strong>{b.title}</strong>
              <ul style={{ margin: "0.25rem 0 0" }}>
                {b.criterionChanges.map((c) => (
                  <li key={c.criterionId} style={{ fontSize: "var(--lf-body-size)" }}>
                    {c.criterionId}:{" "}
                    {c.fromApplicability !== c.toApplicability ? (
                      <span data-testid={`applicability-change-${c.criterionId}`}>
                        {c.fromApplicability} → {c.toApplicability} (state change)
                      </span>
                    ) : (
                      <>score {num(c.fromScore)} → {num(c.toScore)} ({delta(c.scoreDelta)})</>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {m.blocks.every((b) => b.criterionChanges.length === 0) && (
            <p style={{ color: "var(--text-muted)", margin: "0.35rem 0 0" }}>No criterion-level changes.</p>
          )}
        </details>
      </section>

      <section className="card" style={{ marginTop: "1rem" }} data-testid="finding-movement">
        <h2 style={{ marginTop: 0 }}>Engine findings — resolved, persisting, new</h2>
        <p style={{ marginTop: 0, color: "var(--text-muted)", fontSize: "var(--lf-body-size)" }}>
          Matched by the stable engine finding key. A persisting finding shows
          both sides' expression — identity stability must not conceal changed
          severity or text.
        </p>
        {m.findings.length === 0 ? (
          <p style={{ margin: 0, color: "var(--text-muted)" }}>Neither side raised engine findings.</p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
            {m.findings.map((f) => (
              <FindingRowView key={f.key} f={f} />
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

const BUCKET_STYLE: Record<FindingComparison["bucket"], { label: string; color: string }> = {
  resolved: { label: "RESOLVED", color: "var(--state-ok)" },
  persisting: { label: "PERSISTING", color: "var(--lf-amber-deep)" },
  new: { label: "NEW", color: "var(--state-danger)" },
};

function FindingRowView({ f }: { f: FindingComparison }) {
  const s = BUCKET_STYLE[f.bucket];
  return (
    <li style={{ marginBottom: "0.4rem" }} data-testid={`finding-${f.bucket}-${f.key}`}>
      <strong style={{ color: s.color }}>{s.label}</strong>{" "}
      <code>{f.key}</code>
      {f.bucket === "persisting" ? (
        <div style={{ fontSize: "var(--lf-body-size)" }}>
          Baseline ({f.baseline!.severity}): {f.baseline!.text}
          <br />
          Current ({f.current!.severity}): {f.current!.text}
          {f.changedExpression && (
            <em style={{ color: "var(--lf-amber-deep)" }}> — the engine's wording changed between sides</em>
          )}
        </div>
      ) : (
        <div style={{ fontSize: "var(--lf-body-size)" }}>
          {(f.baseline ?? f.current)!.severity}: {(f.baseline ?? f.current)!.text}
        </div>
      )}
    </li>
  );
}
